import { useEffect, useId, useState } from "react";
import {
  ALL_ACCOUNTS,
  bracketWords,
  parseTicks,
  pointsToTicks,
  presetSummary,
  resolvePreset,
  ticketBracket,
  type BracketPreset,
  type ContractFacts,
  type EntryType,
  type TradeSide,
} from "@helm/core";
import {
  CANCEL_ALL_COMPACT_LABEL,
  CANCEL_ALL_LABEL,
  FLAT,
  MINUS,
  REVERSE_LABEL,
  checkPrice,
  closeLabel,
  flattenLabel,
  formatTicks,
  priceToTicks,
  stepPrice,
  tickSize as tickOf,
} from "@helm/ui";
import { useMarket } from "../md/marketData";
import { Tabs, tabPanelProps } from "../components/Tabs";
import { QtyStepper } from "../components/QtyStepper";
import { TradeButton } from "../components/TradeButton";
import { Icon } from "../components/Icon";
import { DomLadder } from "./DomLadder";
import { useOrderEntry, useEntryBusy, type OrderEntry } from "../trading/useOrderEntry";
import { useHotkeyTarget } from "../trading/hotkeys";
import { anchorOf } from "../trading/hosts";
import { useBusy } from "../trading/control";
import { SimBadge } from "../trading/SimBadge";
import { OneClickToggle } from "../trading/OneClickToggle";
import { QtyLimitBadge } from "../trading/QtyLimitBadge";
import { AccountSelect } from "../trading/AccountSelect";
import { CoachEntryGuard } from "../coach/coachGate";
import { BracketSelect, useSelectedPreset, type GhostSide } from "../brackets/BracketSelect";
import { contractFacts } from "../brackets/bracketStore";
import { usePopover } from "../components/Popover";
import { useInDock } from "../workspace/dockContext";

export type OrderPadMode = "scalp" | "dom" | "order";
export type DomControlVisibility = {
  market?: boolean;
  quantity?: boolean;
  bracket?: boolean;
  ladderOnly?: boolean;
};

const MODES: ReadonlyArray<readonly [OrderPadMode, string]> = [
  ["scalp", "Scalp"],
  ["dom", "DOM"],
  ["order", "Order"],
];

/**
 * Tick sizes for common roots, used for price-field validation until `InstrumentInfo` arrives from
 * the md stream (which always wins). Reference data proper comes from the ticker plant (§6.6).
 * Orders are never sent on a fallback tick: they need the instrument (gate `NO_INSTRUMENT`).
 */
const FALLBACK_TICK: Readonly<Record<string, number>> = {
  NQ: 0.25,
  MNQ: 0.25,
  ES: 0.25,
  MES: 0.25,
  RTY: 0.1,
  M2K: 0.1,
  YM: 1,
  MYM: 1,
  CL: 0.01,
  MCL: 0.01,
  GC: 0.1,
  MGC: 0.1,
};

/**
 * Order pad (Tradesea Scalp | DOM | Order; SPEC §9.2). Live on the local mock and Rithmic Test
 * only (owner 2026-09-27, ADR-0021): every button asks the gate (`trading/gate.ts`) and says why
 * it is off. Orders go through the trading socket's `PlaceOrder` / `ExitAction`, one per click.
 */
export function OrderPad({
  symbol,
  mode,
  onModeChange,
  domJoinCollapsed,
  onDomJoinCollapsedChange,
  domControls,
  onDomControlsChange,
}: {
  symbol: string | null;
  mode: OrderPadMode;
  onModeChange: (m: OrderPadMode) => void;
  domJoinCollapsed?: boolean;
  onDomJoinCollapsedChange?: (collapsed: boolean) => void;
  domControls?: DomControlVisibility | undefined;
  onDomControlsChange?: ((controls: DomControlVisibility) => void) | undefined;
}): React.JSX.Element {
  const idBase = useId();
  const inDock = useInDock();
  const [qty, setQty] = useState(1);
  const [ghost, setGhost] = useState<GhostSide>(null);
  const [localControls, setLocalControls] = useState<DomControlVisibility>({});
  const [localJoinCollapsed, setLocalJoinCollapsed] = useState(false);
  const controls = domControls ?? localControls;
  const joinCollapsed = domJoinCollapsed ?? localJoinCollapsed;
  const display = usePopover();
  const setControl = (key: keyof DomControlVisibility, value: boolean): void => {
    (onDomControlsChange ?? setLocalControls)({ ...controls, [key]: value });
  };
  const setJoinCollapsed = onDomJoinCollapsedChange ?? setLocalJoinCollapsed;
  const showMarket = !controls.ladderOnly && controls.market !== false;
  const showQuantity = !controls.ladderOnly && controls.quantity !== false;
  const showBracket = !controls.ladderOnly && controls.bracket !== false;
  const showJoin = !controls.ladderOnly && !joinCollapsed;
  const bracket = useSelectedPreset(symbol);
  // Like a Tradovate ATM strategy, the preset sizes the order: its quantity becomes the pad's
  // whenever the chosen preset (or its quantity) changes, including on load and symbol switch.
  const presetQty = bracket?.qty ?? null;
  useEffect(() => {
    if (presetQty !== null) setQty(presetQty);
  }, [bracket?.id, presetQty]);
  // The selected preset rides along with every new entry (ADR-0025; sized in brackets/order.ts).
  const facts = contractFacts(symbol, useMarket(symbol)?.info);
  const e = useOrderEntry(symbol, mode === "order" ? "TICKET" : "SCALP_PAD", { bracket, facts });
  const ctxId = useHotkeyTarget((action, el) => {
    e.hotkey(action, qty, el);
  });
  const fmt = e.fmt;
  const contract = e.contract ?? symbol ?? "—";
  const exchange = e.m?.info?.exchange ?? "";
  const bid = formatTicks(e.bid, fmt);
  const ask = formatTicks(e.ask, fmt);
  const tick = fmt
    ? tickOf(fmt)
    : symbol !== null
      ? (FALLBACK_TICK[symbol.replace(/[FGHJKMNQUVXZ]\d{1,2}$/, "")] ?? null)
      : null;

  return (
    <div className="pad" data-trading-scope="pad" data-trading-ctx={ctxId} tabIndex={-1}>
      <Tabs
        tabs={MODES}
        value={mode}
        onChange={onModeChange}
        label="Order mode"
        idBase={idBase}
        variant="segmented"
      />
      {/*
       * The account this pad trades leads its order row (owner 2026-10-04). The instrument is the
       * module header's picker; the trading state and 1-click are shown once for the window
       * (status line). Outside a dock (mobile) the contract, trading state and 1-click stay here.
       */}
      <div className="pad-contract" title={exchange ? `${exchange}:${contract}` : contract}>
        <AccountSelect />
        {!inDock && <span className="pad-symbol">{exchange ? `${exchange}:${contract}` : contract}</span>}
        {!inDock && <SimBadge compact />}
        {!inDock && <OneClickToggle compact />}
        <QtyLimitBadge accountId={e.accountId} qty={qty} />
      </div>
      <PositionChip e={e} />

      {mode === "scalp" && (
        <div className="pad-body" {...tabPanelProps(idBase, "scalp")}>
          {/* A coach P1 card covers only these entry controls; the exits below stay usable. */}
          <CoachEntryGuard accountId={e.accountId}>
            <div className="scalp-row">
              <MarketButton e={e} side="buy" qty={qty} size="lg">
                Buy
              </MarketButton>
              <QtyStepper value={qty} onChange={setQty} compact />
              <MarketButton e={e} side="sell" qty={qty} size="lg">
                Sell
              </MarketButton>
            </div>
            <div className="scalp-row scalp-join">
              <JoinButton e={e} side="buy" qty={qty}>
                Join Bid <span className="num tone-pos">{bid}</span>
              </JoinButton>
              <JoinButton e={e} side="sell" qty={qty}>
                Join Ask <span className="num tone-neg">{ask}</span>
              </JoinButton>
            </div>
          </CoachEntryGuard>
          <ExitGrid e={e} />
        </div>
      )}

      {mode === "dom" && (
        <div className="pad-body pad-body-dom" {...tabPanelProps(idBase, "dom")}>
          {/*
           * Which controls show is a module option: in a dock it lives in the header "⋯" menu
           * (`ModuleMenu`); outside one (mobile) the "Show ▾" menu stays here.
           */}
          {(!inDock || !showQuantity || !showBracket) && (
            <div className="pad-dom-display">
              <span
                className="pad-dom-context num"
                title={`Order quantity ${String(qty)}; ${bracket ? `bracket ${bracket.name}` : "no bracket"}`}
              >
                {!showQuantity && `Qty ${String(qty)}`}
                {!showQuantity && !showBracket && " · "}
                {!showBracket && (bracket?.name ?? "Bracket: none")}
              </span>
              {!inDock && (
                <div className="pad-dom-display-menu">
                  <button type="button" className="btn btn-sm" {...display.triggerProps}>
                    Show ▾
                  </button>
                  {display.open && (
                    <div
                      className="pad-dom-options"
                      role="group"
                      aria-label="DOM control visibility"
                      {...display.panelProps}
                    >
                      <DomControlsOptions
                        controls={controls}
                        joinCollapsed={joinCollapsed}
                        onControl={setControl}
                        onJoinCollapsed={setJoinCollapsed}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          {/* Compact controls above a tall ladder (owner: "DOM taller, buy/sell more compact, like Tradovate"). */}
          {showMarket && (
            <div className="pad-grid pad-dom-market">
              <MarketButton e={e} side="buy" qty={qty} size="sm">
                Buy Mkt +{qty}
              </MarketButton>
              <MarketButton e={e} side="sell" qty={qty} size="sm">
                Sell Mkt {MINUS}
                {qty}
              </MarketButton>
            </div>
          )}
          {showQuantity && (
            <div className="pad-dom-quantity">
              <QtyStepper value={qty} onChange={setQty} favorites />
              {!controls.ladderOnly && (
                <button
                  type="button"
                  className="btn btn-sm"
                  aria-expanded={!joinCollapsed}
                  onClick={() => {
                    setJoinCollapsed(!joinCollapsed);
                  }}
                >
                  {joinCollapsed ? "Join ▸" : "Join ▾"}
                </button>
              )}
            </div>
          )}
          {showJoin && (
            <div className="pad-grid pad-dom-join">
              <JoinButton e={e} side="buy" qty={qty} size="sm">
                Join Bid
              </JoinButton>
              <JoinButton e={e} side="sell" qty={qty} size="sm">
                Join Ask
              </JoinButton>
            </div>
          )}
          {/*
           * Exits right below the entry rows (owner 2026-10-04), behind a divider so a risk action is
           * never adjacent to an entry button; the ladder below takes the rest of the panel.
           */}
          <ExitGrid e={e} compact />
          {/* Tradovate-style bracket strategy (ATM) picker, SPEC §9.4. Brackets are not routed yet. */}
          {showBracket && (
            <BracketSelect symbol={symbol} facts={facts} qty={qty} ghost={ghost} onGhost={setGhost} />
          )}
          <div className="pad-ladder">
            <DomLadder
              symbol={symbol}
              compact
              qty={qty}
              ghost={bracket && ghost ? { preset: bracket, side: ghost } : null}
            />
          </div>
        </div>
      )}

      {mode === "order" && (
        <div className="pad-body" {...tabPanelProps(idBase, "order")}>
          <CoachEntryGuard accountId={e.accountId}>
            <OrderForm
              e={e}
              contract={contract}
              tick={tick}
              qty={qty}
              setQty={setQty}
              bracketSlot={<BracketSelect symbol={symbol} facts={facts} qty={qty} />}
              bracket={bracket}
              facts={facts}
            />
          </CoachEntryGuard>
        </div>
      )}
    </div>
  );
}

/**
 * Which DOM-mode controls show (presentation only; exits always stay): the module "⋯" menu in a
 * dock (`ModuleMenu`), the pad's own "Show ▾" outside one.
 */
export function DomControlsOptions({
  controls,
  joinCollapsed,
  onControl,
  onJoinCollapsed,
}: {
  controls: DomControlVisibility;
  joinCollapsed: boolean;
  onControl: (key: keyof DomControlVisibility, value: boolean) => void;
  onJoinCollapsed: (collapsed: boolean) => void;
}): React.JSX.Element {
  return (
    <>
      <label className="check-row">
        <input
          type="checkbox"
          checked={controls.ladderOnly === true}
          onChange={(event) => {
            onControl("ladderOnly", event.target.checked);
          }}
        />
        Ladder only
      </label>
      <span className="muted">Exits always stay visible.</span>
      {(
        [
          ["market", "Market buttons"],
          ["quantity", "Quantity"],
          ["bracket", "Bracket"],
        ] as const
      ).map(([key, label]) => (
        <label key={key} className="check-row">
          <input
            type="checkbox"
            checked={controls[key] !== false}
            disabled={controls.ladderOnly === true}
            onChange={(event) => {
              onControl(key, event.target.checked);
            }}
          />
          {label}
        </label>
      ))}
      <label className="check-row">
        <input
          type="checkbox"
          checked={!joinCollapsed}
          disabled={controls.ladderOnly === true}
          onChange={(event) => {
            onJoinCollapsed(!event.target.checked);
          }}
        />
        Join row
      </label>
    </>
  );
}

/** Live position chip (SPEC §9.2): `+2 @ 21,450.25`. */
function PositionChip({ e }: { e: OrderEntry }): React.JSX.Element {
  const p = e.pos;
  if (!p) return <div className="pad-position muted">{FLAT}</div>;
  return (
    <div className={`pad-position num ${p.netQty > 0 ? "tone-pos" : "tone-neg"}`} data-net={p.netQty}>
      {p.netQty > 0 ? "+" : MINUS}
      {Math.abs(p.netQty)} {p.symbol}
    </div>
  );
}

function MarketButton({
  e,
  side,
  qty,
  size,
  children,
}: {
  e: OrderEntry;
  side: TradeSide;
  qty: number;
  size: "sm" | "lg";
  children: React.ReactNode;
}): React.JSX.Element {
  const busy = useEntryBusy(e, side === "buy" ? "buy-mkt" : "sell-mkt");
  const contract = e.contract ?? "the contract";
  return (
    <TradeButton
      tone={side}
      size={size}
      semantics={`${side === "buy" ? "Buys" : "Sells"} ${String(qty)} ${contract} at market`}
      hotkey={side === "buy" ? "B" : "S"}
      gate={e.placeGate(side, qty)}
      busy={busy}
      onPress={(ev) => {
        e.market(side, qty, anchorOf(ev));
      }}
    >
      {children}
    </TradeButton>
  );
}

function JoinButton({
  e,
  side,
  qty,
  size = "md",
  children,
}: {
  e: OrderEntry;
  side: TradeSide;
  qty: number;
  size?: "sm" | "md";
  children: React.ReactNode;
}): React.JSX.Element {
  const busy = useEntryBusy(e, side === "buy" ? "join-bid" : "join-ask");
  const px = side === "buy" ? e.bid : e.ask;
  return (
    <TradeButton
      size={size}
      semantics={`${side === "buy" ? "Buy" : "Sell"} limit ${String(qty)} at the best ${side === "buy" ? "bid" : "ask"} ${formatTicks(px, e.fmt)}`}
      hotkey={side === "buy" ? "J" : "A"}
      gate={e.placeGate(side, qty, true, px)}
      busy={busy}
      onPress={(ev) => {
        e.join(side, qty, anchorOf(ev));
      }}
    >
      {children}
    </TradeButton>
  );
}

/**
 * Exit/cancel strip (SPEC §9.7 semantics; labels name the scope, owner 2026-10-04). "Close NQ" =
 * Exit at Mkt & Cxl for this contract (X); "Reverse" = Reverse & Cxl (R: cancels the contract's
 * orders, then reverses the confirmed position with one market order of twice its size); "Cancel
 * all" = the account's working orders (C; "Cxl all" in the compact DOM strip); "Flatten acct" =
 * Exit All Positions & Cxl All Orders on the account (F), which becomes "Flatten all" — every
 * account, its own confirmation preference — while All accounts is selected. Danger colours, never
 * the Buy / Sell ones: outlined exits, solid Flatten.
 */
export function ExitGrid({ e, compact = false }: { e: OrderEntry; compact?: boolean }): React.JSX.Element {
  const contract = e.contract ?? "the contract";
  const exitBusy = useEntryBusy(e, "exit");
  const cxlBusy = useEntryBusy(e, "cxl-all");
  const flatBusy = useEntryBusy(e, "flatten");
  const revBusy = useEntryBusy(e, "reverse");
  const allBusy = useBusy("flatten-all");
  const all = e.account === ALL_ACCOUNTS;
  return (
    <div className={`pad-grid pad-exits${compact ? " pad-exits-compact" : ""}`}>
      <TradeButton
        size="sm"
        tone="exit"
        semantics={`Exit at Mkt & Cxl: cancels ${contract} working orders, then closes the ${contract} position at market`}
        hotkey="X"
        label={`Exit at Mkt & Cxl ${contract}`}
        gate={e.exitGate("exitSymbol")}
        busy={exitBusy}
        onPress={e.exitSymbol}
      >
        {closeLabel(e.contract)}
      </TradeButton>
      <TradeButton
        size="sm"
        tone="exit"
        semantics={`Reverse & Cxl: cancels ${contract} orders, then reverses the confirmed position (one market order of twice its size)`}
        hotkey="R"
        label={`Reverse & Cxl ${contract}`}
        gate={e.exitGate("reverse")}
        busy={revBusy}
        onPress={(ev) => {
          e.reverse(anchorOf(ev));
        }}
      >
        {REVERSE_LABEL}
      </TradeButton>
      <TradeButton
        size="sm"
        tone="exit"
        semantics="Cxl All: cancels every working order on the account; positions untouched"
        hotkey="C"
        gate={e.exitGate("cancelAll")}
        busy={cxlBusy}
        onPress={e.cancelAll}
      >
        {compact ? CANCEL_ALL_COMPACT_LABEL : CANCEL_ALL_LABEL}
      </TradeButton>
      <TradeButton
        size="sm"
        tone="flatten"
        semantics={
          all
            ? "Exit All — all accounts (Exit all accts): cancels every order and closes every position on every account; asks first when 'Ask before Exit All' is on (Settings)"
            : "Exit All Positions & Cxl All Orders (Exit all): cancels all orders and closes every position on the account"
        }
        hotkey={all ? "Ctrl+Shift+F" : "F"}
        label={all ? "Exit All — all accounts" : "Exit All Positions & Cxl All Orders"}
        gate={all ? e.flattenAllGate : e.exitGate("flattenAccount")}
        busy={all ? allBusy : flatBusy}
        onPress={e.flatten}
      >
        {flattenLabel(all)}
      </TradeButton>
    </div>
  );
}

type Unit = "ticks" | "points" | "price";
type FormType = "market" | "limit" | "stop";

function OrderForm({
  e,
  contract,
  tick,
  qty,
  setQty,
  bracketSlot,
  bracket,
  facts,
}: {
  e: OrderEntry;
  contract: string;
  tick: number | null;
  qty: number;
  setQty: (q: number) => void;
  /** The bracket-strategy picker; a chosen preset replaces the one-off SL/TP below. */
  bracketSlot: React.ReactNode;
  bracket: BracketPreset | null;
  facts: ContractFacts | null;
}): React.JSX.Element {
  const id = useId();
  const [side, setSide] = useState<TradeSide>("buy");
  const [type, setType] = useState<FormType>("limit");
  const [stopLimit, setStopLimit] = useState(false);
  const [limitPx, setLimitPx] = useState("");
  const [stopPx, setStopPx] = useState("");
  const [tif, setTif] = useState<"DAY" | "GTC">("DAY");
  const [sl, setSl] = useState({ on: false, value: "12", unit: "ticks" as Unit, trail: false });
  const [tp, setTp] = useState({ on: false, value: "24", unit: "ticks" as Unit, trail: false });
  const busy = useEntryBusy(e, "ticket");

  const needsLimit = type === "limit" || (type === "stop" && stopLimit);
  const needsStop = type === "stop";
  const limitCheck = needsLimit && limitPx !== "" ? checkPrice(limitPx, tick ?? 0) : null;
  const stopCheck = needsStop && stopPx !== "" ? checkPrice(stopPx, tick ?? 0) : null;
  // Exact integer ticks from the instrument's rational tick size (never the fallback table).
  const toTicks = (s: string) => (e.fmt ? priceToTicks(s, e.fmt.tickSizeNum, e.fmt.tickSizeDen) : null);
  const limitTicks = needsLimit ? toTicks(limitPx) : null;
  const stopTicks = needsStop ? toTicks(stopPx) : null;
  const entryType: EntryType =
    type === "market" ? "market" : type === "limit" ? "limit" : stopLimit ? "stopLimit" : "stop";

  const typeLabel =
    type === "market" ? "Market" : type === "limit" ? "Limit" : stopLimit ? "Stop Limit" : "Stop";
  const price = limitTicks?.ok ? limitTicks.ticks : stopTicks?.ok ? stopTicks.ticks : null;
  // One-off protection (no preset): each toggle as a tick distance from the entry. A "price" is
  // measured from the entry's price, or the touch it would buy/sell at for a market order.
  const reference = type === "market" ? (side === "buy" ? e.ask : e.bid) : price;
  const sign = side === "buy" ? 1 : -1;
  const slDist = bracket || !sl.on ? null : protectionTicks(sl, "stop", sign, reference, e, facts);
  const tpDist = bracket || !tp.on ? null : protectionTicks(tp, "target", sign, reference, e, facts);
  const oneOff =
    slDist?.ok !== false && tpDist?.ok !== false
      ? ticketBracket(slDist?.ticks ?? null, tpDist?.ticks ?? null, sl.trail, qty)
      : null;
  const words = [
    `${side === "buy" ? "Buy" : "Sell"} ${String(qty)} ${contract} ${typeLabel}`,
    needsStop && stopPx ? `stop ${stopPx}` : "",
    needsLimit && limitPx ? `limit ${limitPx}` : "",
    bracket ? `· bracket ${bracket.name} (${bracketSummaryFor(bracket, facts)})` : "",
    oneOff ? `· ${bracketWords(oneOff)}` : "",
  ]
    .filter(Boolean)
    .join(" ");

  // The form's own problems come first; then the trading gate.
  const priceProblem = (label: string, t: ReturnType<typeof toTicks>): string | null =>
    t === null ? `${label} price needs the contract's tick size` : t.ok ? null : t.message;
  const formProblem =
    (needsStop ? priceProblem("Stop", stopTicks) : null) ??
    (needsLimit ? priceProblem("Limit", limitTicks) : null) ??
    (slDist && !slDist.ok ? `Stop Loss: ${slDist.message}` : null) ??
    (tpDist && !tpDist.ok ? `Take Profit: ${tpDist.message}` : null);
  const tradeGate = e.placeGate(side, qty, type !== "market", type === "market" ? null : price);
  const gate =
    formProblem === null ? tradeGate : ({ ok: false, code: "NO_PRICE", reason: formProblem } as const);

  return (
    <form
      className="order-form"
      aria-label="Order ticket"
      onSubmit={(ev) => {
        ev.preventDefault();
      }}
    >
      <div className="seg-group" role="radiogroup" aria-label="Side">
        {(["buy", "sell"] as const).map((s) => (
          <button
            key={s}
            type="button"
            role="radio"
            aria-checked={side === s}
            className={`seg seg-${s}${side === s ? " active" : ""}`}
            onClick={() => {
              setSide(s);
            }}
          >
            {s === "buy" ? "Buy / Long" : "Sell / Short"}
          </button>
        ))}
      </div>
      <div className="seg-group" role="radiogroup" aria-label="Order type">
        {(["market", "limit", "stop"] as const).map((t) => (
          <button
            key={t}
            type="button"
            role="radio"
            aria-checked={type === t}
            className={type === t ? "seg active" : "seg"}
            onClick={() => {
              setType(t);
            }}
          >
            {t[0]?.toUpperCase()}
            {t.slice(1)}
          </button>
        ))}
      </div>
      {type === "stop" && (
        <label className="check-row">
          <input
            type="checkbox"
            checked={stopLimit}
            onChange={(ev) => {
              setStopLimit(ev.target.checked);
            }}
          />
          <span>Stop Limit</span>
        </label>
      )}
      {needsStop && (
        <PriceField
          id={`${id}-stop`}
          label="Stop price"
          value={stopPx}
          onChange={setStopPx}
          tick={tick}
          error={stopCheck && !stopCheck.ok ? stopCheck.message : null}
        />
      )}
      {needsLimit && (
        <PriceField
          id={`${id}-limit`}
          label="Limit price"
          value={limitPx}
          onChange={setLimitPx}
          tick={tick}
          error={limitCheck && !limitCheck.ok ? limitCheck.message : null}
        />
      )}
      <div className="field">
        <span>Quantity</span>
        <QtyStepper value={qty} onChange={setQty} />
      </div>
      {bracketSlot}
      {bracket === null && (
        <>
          <Protection label="Stop Loss" state={sl} onChange={setSl} trail />
          <Protection label="Take Profit" state={tp} onChange={setTp} />
        </>
      )}
      <label className="field field-inline">
        <span>TIF</span>
        <select
          className="select select-sm"
          value={tif}
          onChange={(ev) => {
            setTif(ev.target.value === "GTC" ? "GTC" : "DAY");
          }}
        >
          <option value="DAY">Day</option>
          <option value="GTC">GTC</option>
        </select>
      </label>
      <div className="order-words" aria-live="polite">
        {words}
      </div>
      <div className="pad-grid">
        <TradeButton
          tone={side}
          semantics={`Submits: ${words}`}
          gate={gate}
          busy={busy}
          onPress={(ev) => {
            e.place(
              "ticket",
              {
                side,
                type: entryType,
                qty,
                origin: "TICKET",
                tif,
                ...(needsLimit && limitTicks?.ok ? { limitTicks: limitTicks.ticks } : {}),
                ...(needsStop && stopTicks?.ok ? { stopTicks: stopTicks.ticks } : {}),
                // A preset (bracket) rides along by itself; otherwise the one-off SL/TP, or none.
                ...(bracket ? {} : { bracket: oneOff }),
              },
              anchorOf(ev),
            );
          }}
        >
          Confirm
        </TradeButton>
        <button
          type="reset"
          className="btn"
          onClick={() => {
            setLimitPx("");
            setStopPx("");
            setSl((s) => ({ ...s, on: false }));
            setTp((s) => ({ ...s, on: false }));
          }}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

function PriceField({
  id,
  label,
  value,
  onChange,
  tick,
  error,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  tick: number | null;
  error: string | null;
}): React.JSX.Element {
  const step = (n: number): void => {
    if (tick === null) return;
    const c = checkPrice(value, tick);
    if (c.ok) onChange(String(stepPrice(c.price, n, tick)));
  };
  return (
    <div className="field">
      <label htmlFor={id}>
        {label}
        {tick !== null && <span className="muted"> · tick {tick}</span>}
      </label>
      <div className="price-input">
        <button
          type="button"
          className="qty-btn"
          aria-label={`${label} down one tick`}
          onClick={() => {
            step(-1);
          }}
          disabled={tick === null}
        >
          <Icon name="minus" size={12} />
        </button>
        <input
          id={id}
          className="input num"
          inputMode="decimal"
          placeholder={tick === null ? "tick size unknown" : "0.00"}
          value={value}
          aria-invalid={error !== null}
          aria-describedby={error ? `${id}-err` : undefined}
          onChange={(ev) => {
            onChange(ev.target.value);
          }}
          onKeyDown={(ev) => {
            if (ev.key === "ArrowUp") {
              ev.preventDefault();
              step(1);
            } else if (ev.key === "ArrowDown") {
              ev.preventDefault();
              step(-1);
            }
          }}
        />
        <button
          type="button"
          className="qty-btn"
          aria-label={`${label} up one tick`}
          onClick={() => {
            step(1);
          }}
          disabled={tick === null}
        >
          <Icon name="plus" size={12} />
        </button>
      </div>
      {error && (
        <span id={`${id}-err`} className="field-error" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

type Prot = { on: boolean; value: string; unit: Unit; trail: boolean };

/** The template's summary for this contract (a points / $ template re-derived — ADR-0049). */
function bracketSummaryFor(p: BracketPreset, facts: ContractFacts | null): string {
  const r = resolvePreset(p, facts);
  return r.ok ? presetSummary(r.preset) : r.message;
}

/**
 * A protection toggle's distance from the entry in whole ticks: ticks as typed, points on the tick
 * grid (never rounded), or an absolute price on the right side of `reference` (the entry price, or
 * the touch for a market order).
 */
function protectionTicks(
  p: Prot,
  role: "stop" | "target",
  sign: 1 | -1,
  reference: number | null,
  e: OrderEntry,
  facts: ContractFacts | null,
): { ok: true; ticks: number } | { ok: false; message: string } {
  if (p.unit === "ticks") return parseTicks(p.value);
  if (p.unit === "points") {
    return facts === null ? { ok: false, message: "Tick size unknown" } : pointsToTicks(p.value, facts);
  }
  if (e.fmt === null) return { ok: false, message: "Tick size unknown" };
  const px = priceToTicks(p.value, e.fmt.tickSizeNum, e.fmt.tickSizeDen);
  if (!px.ok) return { ok: false, message: px.message };
  if (reference === null) return { ok: false, message: "No entry price yet to measure from" };
  const dist = (px.ticks - reference) * sign * (role === "target" ? 1 : -1);
  return dist >= 1
    ? { ok: true, ticks: dist }
    : {
        ok: false,
        message:
          role === "stop"
            ? "Price is not below a long entry / above a short one"
            : "Price is not beyond the entry",
      };
}

function Protection({
  label,
  state,
  onChange,
  trail = false,
}: {
  label: string;
  state: Prot;
  onChange: (p: Prot) => void;
  /** Offer "Trail" (the stop only: it trails from the first tick of profit at its distance). */
  trail?: boolean;
}): React.JSX.Element {
  const id = useId();
  return (
    <div className="protection">
      <label className="check-row">
        <input
          type="checkbox"
          checked={state.on}
          onChange={(ev) => {
            onChange({ ...state, on: ev.target.checked });
          }}
        />
        <span>{label}</span>
      </label>
      {state.on && (
        <div className="protection-row">
          <input
            id={`${id}-v`}
            className="input input-sm num"
            inputMode="decimal"
            aria-label={`${label} distance`}
            value={state.value}
            onChange={(ev) => {
              onChange({ ...state, value: ev.target.value });
            }}
          />
          <select
            className="select select-sm"
            aria-label={`${label} unit`}
            value={state.unit}
            onChange={(ev) => {
              onChange({ ...state, unit: ev.target.value as Unit });
            }}
          >
            <option value="ticks">Ticks</option>
            <option value="points">Points</option>
            <option value="price">Price</option>
          </select>
          {trail && (
            <label
              className="check-row"
              title="Trail the stop at this distance from the first tick of profit"
            >
              <input
                type="checkbox"
                checked={state.trail}
                onChange={(ev) => {
                  onChange({ ...state, trail: ev.target.checked });
                }}
              />
              <span>Trail</span>
            </label>
          )}
        </div>
      )}
    </div>
  );
}
